// ---------------------------------------------------------------------------
// Shared types & initial-state factory for the teamFocusSession slice.
// ---------------------------------------------------------------------------

// --- API Response Types ---

export interface TeamFocusSessionParticipant {
    id: string;
    userId: string;
    role: 'host' | 'member';
    status: 'active' | 'left' | 'disconnected';
    joinedAt: string; // ISO string
    leftAt: string | null;
}

export interface ApiTeamFocusSession {
    id: string;
    hostId: string;
    projectId: string;
    duration: number | null;
    goal?: string;
    startTime: string | null; // ISO string
    endTime: string | null; // ISO string
    status: 'waiting' | 'active' | 'paused' | 'completed' | 'ended';
    roomId: string;
    rtcToken?: string;
    preApprovedUsers: string[];
    participants: TeamFocusSessionParticipant[];
    createdAt: string; // ISO string
    updatedAt: string; // ISO string
}

// --- Redux State Types ---

export interface Task {
    id: string;
    title: string;
    description?: string;
    status: "todo" | "inprogress" | "done";
    completed?: boolean;
    createdAt: string; // ISO string (serializable)
    updatedAt?: string;
    created_by: { id: string; name: string } | string;
    assigned_to?: { id: string; name: string } | string | null;
    assignedTo?: { id: string; name: string; avatar: string } | null;
    is_public: boolean;
    roomId?: string;
    text?: string;
}

export interface NoteCreator {
    id: string;
    name: string;
}

export interface Note {
    id: string;
    note: string;
    createdAt: string; // ISO string
    is_public: boolean;
    created_by: string;
    creator: NoteCreator;
}

export interface Reminder {
    id: string;
    text: string;
    time: string;
    completed: boolean;
}

export interface TeamCheckin {
    id: string;
    title: string;
    checkinTime: string;
    created_by: string;
    is_public: boolean;
    roomId: string;
}

export interface TeamMember {
    id: string;
    name: string;
    avatar: string;
    isOnline: boolean;
    isMuted: boolean;
}

export interface TeamSessionState {
    // API session data
    sessionId: string | null;
    hostId: string | null;
    projectId: string | null;
    roomId: string | null;
    status: 'waiting' | 'active' | 'paused' | 'completed' | 'ended';
    participants: TeamFocusSessionParticipant[];

    // Goal & settings
    goal: string;
    duration: number; // minutes

    currentRoom: any | null;
    isHost: boolean;

    // Elapsed time tracking (no timerStartedAt/timerDuration from API)
    sessionStartedAt: string | null; // ISO string when session started

    // Session ended by host flag
    sessionEndedByHost: boolean;

    // Tools state
    tasks: Task[];
    notes: Note[];
    reminders: Reminder[];
    checkins: TeamCheckin[];
    teamMembers: TeamMember[];

    // Loading / auxiliary flags
    notesLoading: boolean;
    checkinsLoading: boolean;
    tasksLoading: boolean;
    tasksError: string | null;

    // RTC token
    rtcToken: string | null;
    rtcTokenLoading: boolean;
    rtcTokenError: string | null;

    // Settings
    breakRemindersEnabled: boolean;
}

export interface TeamSessionsRootState {
    sessions: Record<string, TeamSessionState>;
}

export const createInitialTeamSessionState = (): TeamSessionState => ({
    sessionId: null,
    hostId: null,
    projectId: null,
    roomId: null,
    status: 'waiting',
    participants: [],
    goal: "Focus Session",
    duration: 25,
    currentRoom: null,
    isHost: false,
    sessionStartedAt: null,
    sessionEndedByHost: false,
    tasks: [],
    notes: [],
    reminders: [],
    checkins: [],
    teamMembers: [],
    notesLoading: false,
    checkinsLoading: false,
    tasksLoading: false,
    tasksError: null,
    rtcToken: null,
    rtcTokenLoading: false,
    rtcTokenError: null,
    breakRemindersEnabled: true,
});
